import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import {
  BarChart3,
  TrendingUp,
  Calendar,
  Download,
  Package,
  Wallet,
  ShoppingCart,
  FileSpreadsheet
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from "recharts";

const COLORS = ['#E11D48', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899'];

const DateRangePicker = ({ startDate, endDate, onStartChange, onEndChange }) => {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-[160px] justify-start" data-testid="start-date-btn">
            <Calendar className="w-4 h-4 mr-2" />
            {startDate ? format(startDate, 'dd.MM.yyyy', { locale: de }) : "Von"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="single"
            selected={startDate}
            onSelect={onStartChange}
            locale={de}
          />
        </PopoverContent>
      </Popover>
      <span className="text-slate-400">bis</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-[160px] justify-start" data-testid="end-date-btn">
            <Calendar className="w-4 h-4 mr-2" />
            {endDate ? format(endDate, 'dd.MM.yyyy', { locale: de }) : "Bis"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="single"
            selected={endDate}
            onSelect={onEndChange}
            locale={de}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const ExportButton = ({ startDate, endDate }) => {
  const handleExport = (type) => {
    let url = `${API}/export/${type}`;
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', format(startDate, 'yyyy-MM-dd'));
    if (endDate) params.append('end_date', format(endDate, 'yyyy-MM-dd'));
    if (params.toString()) url += `?${params.toString()}`;
    
    // Open in new tab to download
    window.open(url, '_blank');
    toast.success("Export gestartet");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" data-testid="export-btn">
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport('sales')}>
          <ShoppingCart className="w-4 h-4 mr-2" />
          Verkäufe (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('inventory')}>
          <Package className="w-4 h-4 mr-2" />
          Inventar (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('cash')}>
          <Wallet className="w-4 h-4 mr-2" />
          Kasse (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('products')}>
          <Package className="w-4 h-4 mr-2" />
          Produkte (CSV)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ReportsPage = () => {
  const [salesReport, setSalesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [cashReport, setCashReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeTab, setActiveTab] = useState("sales");

  useEffect(() => {
    fetchReports();
  }, [startDate, endDate]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = {};
      if (startDate) params.start_date = format(startDate, 'yyyy-MM-dd');
      if (endDate) params.end_date = format(endDate, 'yyyy-MM-dd');
      
      const queryString = new URLSearchParams(params).toString();
      
      const [salesRes, invRes, cashRes] = await Promise.all([
        axios.get(`${API}/reports/sales${queryString ? '?' + queryString : ''}`),
        axios.get(`${API}/reports/inventory`),
        axios.get(`${API}/reports/cash${queryString ? '?' + queryString : ''}`)
      ]);
      
      setSalesReport(salesRes.data);
      setInventoryReport(invRes.data);
      setCashReport(cashRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Berichte");
    } finally {
      setLoading(false);
    }
  };

  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Berichte
          </h1>
          <p className="text-slate-500 mt-1">
            Verkaufsstatistiken und Analysen
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartChange={setStartDate}
            onEndChange={setEndDate}
          />
          {(startDate || endDate) && (
            <Button variant="ghost" onClick={clearDates} size="sm">
              Zurücksetzen
            </Button>
          )}
          <ExportButton startDate={startDate} endDate={endDate} />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="sales" data-testid="sales-tab">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Verkäufe
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="inventory-tab">
            <Package className="w-4 h-4 mr-2" />
            Inventar
          </TabsTrigger>
          <TabsTrigger value="cash" data-testid="cash-tab">
            <Wallet className="w-4 h-4 mr-2" />
            Finanzen
          </TabsTrigger>
        </TabsList>

        {/* Sales Tab */}
        <TabsContent value="sales" className="space-y-6">
          {/* Sales Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Gesamtverkäufe</div>
                <div className="text-2xl font-heading font-bold">
                  {salesReport?.total_sales || 0}
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Gesamtumsatz</div>
                <div className="text-2xl font-heading font-bold text-emerald-600">
                  {salesReport?.total_revenue?.toFixed(2) || '0.00'}€
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Durchschn. Verkauf</div>
                <div className="text-2xl font-heading font-bold text-blue-600">
                  {salesReport?.total_sales > 0 
                    ? (salesReport.total_revenue / salesReport.total_sales).toFixed(2) 
                    : '0.00'}€
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sales by Day Chart */}
          {salesReport?.daily_breakdown?.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading">Umsatz nach Tag</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={salesReport.daily_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(val) => format(new Date(val), 'dd.MM', { locale: de })}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value) => [`${value.toFixed(2)}€`, 'Umsatz']}
                        labelFormatter={(label) => format(new Date(label), 'dd.MM.yyyy', { locale: de })}
                      />
                      <Bar dataKey="revenue" fill="#E11D48" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Product Breakdown */}
          {salesReport?.product_breakdown?.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading">Beliebteste Produkte</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={salesReport.product_breakdown.slice(0, 6)}
                          dataKey="quantity_sold"
                          nameKey="product_name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {salesReport.product_breakdown.slice(0, 6).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading">Umsatz nach Produkt</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produkt</TableHead>
                        <TableHead className="text-right">Menge</TableHead>
                        <TableHead className="text-right">Umsatz</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {salesReport.product_breakdown
                        .sort((a, b) => b.revenue - a.revenue)
                        .slice(0, 10)
                        .map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell className="text-right">{item.quantity_sold}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-600">
                              {item.revenue.toFixed(2)}€
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {!salesReport?.product_breakdown?.length && (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Noch keine Verkaufsdaten vorhanden</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Lagerbestand</div>
                <div className="text-2xl font-heading font-bold">
                  {inventoryReport?.total_warehouse || 0} Stück
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Im Automat</div>
                <div className="text-2xl font-heading font-bold text-blue-600">
                  {inventoryReport?.total_automat || 0} Stück
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Gesamtwert</div>
                <div className="text-2xl font-heading font-bold text-emerald-600">
                  {inventoryReport?.total_value?.toFixed(2) || '0.00'}€
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Bestandsübersicht</CardTitle>
            </CardHeader>
            <CardContent>
              {inventoryReport?.items?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produkt</TableHead>
                      <TableHead className="text-right">Lager</TableHead>
                      <TableHead className="text-right">Automat</TableHead>
                      <TableHead className="text-right">Gesamt</TableHead>
                      <TableHead className="text-right">Wert</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventoryReport.items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.product_name}</TableCell>
                        <TableCell className="text-right">{item.warehouse_stock}</TableCell>
                        <TableCell className="text-right">{item.automat_stock}</TableCell>
                        <TableCell className="text-right font-bold">{item.total_stock}</TableCell>
                        <TableCell className="text-right font-mono text-emerald-600">
                          {item.value.toFixed(2)}€
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Keine Inventardaten</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cash Tab */}
        <TabsContent value="cash" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Aktueller Bestand</div>
                <div className="text-2xl font-heading font-bold">
                  {cashReport?.current_balance?.toFixed(2) || '0.00'}€
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Einzahlungen</div>
                <div className="text-2xl font-heading font-bold text-emerald-600">
                  +{cashReport?.deposits?.toFixed(2) || '0.00'}€
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Entnahmen</div>
                <div className="text-2xl font-heading font-bold text-rose-600">
                  -{cashReport?.withdrawals?.toFixed(2) || '0.00'}€
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Verkaufserlöse</div>
                <div className="text-2xl font-heading font-bold text-blue-600">
                  +{cashReport?.sales_revenue?.toFixed(2) || '0.00'}€
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bilanz */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Bilanz</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-50 rounded-md p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-sm text-slate-500">Einnahmen</div>
                    <div className="text-xl font-heading font-bold text-emerald-600">
                      +{((cashReport?.deposits || 0) + (cashReport?.sales_revenue || 0)).toFixed(2)}€
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Ausgaben</div>
                    <div className="text-xl font-heading font-bold text-rose-600">
                      -{cashReport?.withdrawals?.toFixed(2) || '0.00'}€
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Netto</div>
                    <div className={`text-xl font-heading font-bold ${
                      (cashReport?.net_change || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'
                    }`}>
                      {(cashReport?.net_change || 0) >= 0 ? '+' : ''}{cashReport?.net_change?.toFixed(2) || '0.00'}€
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
